/* Files on a message or a devotion — a verse card, a printable notice.
 *
 * SEPARATE FROM THE FEED'S PhotoPicker, deliberately. That one is photos on
 * a post: a grid of thumbnails, four of them, all images. This is a short
 * list that mixes a picture with a PDF and has to say what each channel will
 * actually do with them, which is a different screen rather than a variation
 * on the same one.
 *
 * WHAT THE CHANNELS DO, said here because the admin is deciding here:
 *
 *   In the app   image or PDF
 *   Push         one image, no PDF
 *   SMS          nothing at all
 *
 * The server enforces this too — it has to, since the web is not the only
 * client — but discovering it after pressing Send would be a wasted send.
 */
import { useEffect, useRef, useState } from 'react'
import { AlertCircle, FileText, ImagePlus, Loader2, Paperclip, X } from 'lucide-react'
import { useUploadDocument, useUploadImage } from '@/api/queries'
import { cn } from '@/lib/utils'
import type { Media } from '@/types/api'

export interface PickedFile {
  /** Local key. Not the server id — that arrives once uploaded. */
  key: string
  kind: 'image' | 'document'
  name: string
  /** Object URL for an image; null for a PDF, which has no preview. */
  previewUrl: string | null
  status: 'uploading' | 'done' | 'error'
  mediaId?: string
  error?: string
}

/** Enough for a notice and a picture. More than this on one announcement is
 *  a newsletter, and belongs on the feed. */
export const MAX_FILES = 3

export function useAttachments() {
  const [files, setFiles] = useState<PickedFile[]>([])
  const uploadImage = useUploadImage()
  const uploadDocument = useUploadDocument()

  /* Object URLs are held until revoked. Without this, picking and discarding
     images leaks a file's worth of memory each time. */
  const ref = useRef(files)
  ref.current = files
  useEffect(
    () => () => {
      ref.current.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
      })
    },
    [],
  )

  function add(picked: FileList | null, kind: 'image' | 'document') {
    if (!picked?.length) return
    const room = MAX_FILES - files.length
    const chosen = Array.from(picked).slice(0, room)

    for (const file of chosen) {
      const key = `${file.name}-${file.size}-${Math.random()}`
      const previewUrl = kind === 'image' ? URL.createObjectURL(file) : null
      setFiles((prev) => [
        ...prev,
        { key, kind, name: file.name, previewUrl, status: 'uploading' },
      ])

      const upload = kind === 'image' ? uploadImage : uploadDocument
      upload.mutate(file, {
        onSuccess: (media) =>
          setFiles((prev) =>
            prev.map((f) =>
              f.key === key
                ? { ...f, status: 'done', mediaId: media.id }
                : f,
            ),
          ),
        onError: (err) =>
          setFiles((prev) =>
            prev.map((f) =>
              f.key === key
                ? {
                    ...f,
                    status: 'error',
                    error: (err as Error)?.message ?? 'Could not attach that',
                  }
                : f,
            ),
          ),
      })
    }
  }

  function remove(key: string) {
    setFiles((prev) => {
      prev
        .filter((f) => f.key === key)
        .forEach((f) => {
          if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
        })
      return prev.filter((f) => f.key !== key)
    })
  }

  function reset() {
    files.forEach((f) => {
      if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
    })
    setFiles([])
  }

  return {
    files,
    add,
    remove,
    reset,
    /** Only the ones that landed — a failed upload has no id. */
    mediaIds: files.filter((f) => f.mediaId).map((f) => f.mediaId!),
    /** Sending while an upload is in flight would silently drop it. */
    isUploading: files.some((f) => f.status === 'uploading'),
    isFull: files.length >= MAX_FILES,
    hasDocument: files.some((f) => f.kind === 'document'),
    hasImage: files.some((f) => f.kind === 'image'),
  }
}

export function AttachmentList({
  files,
  onRemove,
  /** Which channels are ticked, so the note under each file can say what
   *  will actually happen to it. */
  channels,
}: {
  files: PickedFile[]
  onRemove: (key: string) => void
  channels: { in_app: boolean; push: boolean; sms: boolean }
}) {
  if (!files.length) return null

  /* The first image is the one a push carries — FCM takes a single picture.
     Naming WHICH one matters when there are two. */
  const firstImageKey = files.find((f) => f.kind === 'image')?.key

  return (
    <ul className="space-y-s2">
      {files.map((file) => (
        <li
          key={file.key}
          className="flex items-center gap-s3 rounded border border-line-2 p-s2"
        >
          {file.kind === 'image' && file.previewUrl ? (
            <img
              src={file.previewUrl}
              alt=""
              className={cn(
                'h-12 w-12 shrink-0 rounded object-cover',
                file.status === 'uploading' && 'opacity-60',
              )}
            />
          ) : (
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded bg-surface-2">
              <FileText className="h-5 w-5 text-ink-2" aria-hidden />
            </span>
          )}

          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold">{file.name}</p>
            <p className="truncate text-sm text-ink-2">
              {file.status === 'uploading' ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                  Attaching
                </span>
              ) : file.status === 'error' ? (
                <span className="inline-flex items-center gap-1 text-danger-ink">
                  <AlertCircle className="h-3 w-3" aria-hidden />
                  {file.error}
                </span>
              ) : (
                <Delivery
                  file={file}
                  channels={channels}
                  isFirstImage={file.key === firstImageKey}
                />
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={() => onRemove(file.key)}
            aria-label={`Remove ${file.name}`}
            className="shrink-0 rounded-pill p-s2 text-ink-2 transition-colors hover:bg-hover hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </li>
      ))}
    </ul>
  )
}

/** What will happen to this particular file, given what is ticked.
 *
 *  Said per file rather than once at the bottom, because the answer differs
 *  between them: with two pictures and a PDF, one picture rides the push and
 *  the other two wait in the app.
 */
function Delivery({
  file,
  channels,
  isFirstImage,
}: {
  file: PickedFile
  channels: { in_app: boolean; push: boolean; sms: boolean }
  isFirstImage: boolean
}) {
  if (file.kind === 'document') {
    if (!channels.in_app) {
      /* The server refuses this, so it is a warning about a send that will
         not go rather than a note about one that will. */
      return (
        <span className="text-warning-ink">
          Tick “In the app” — a push cannot carry a file
        </span>
      )
    }
    return <>Waiting for them in the app</>
  }

  if (channels.push && isFirstImage) {
    return <>Shown on the notification</>
  }
  if (channels.push) {
    return <>In the app — a notification carries one picture</>
  }
  return <>Shown in the app</>
}

/** Files on something already sent or scheduled.
 *
 *  Reading, not editing — a picture shown small and a PDF as a link that
 *  downloads. Separate from AttachmentList above because that one is about
 *  files in the middle of being uploaded, which is a different state.
 */
export function AttachedFiles({ media }: { media: Media[] }) {
  if (!media.length) return null

  return (
    <div className="mt-s3 flex flex-wrap items-center gap-s2">
      {media.map((file) =>
        file.kind === 'document' ? (
          <a
            key={file.id}
            href={file.url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-s2 rounded-pill border border-line-2 px-s3 py-s2 text-sm font-semibold text-ink-2 transition-colors hover:border-brand hover:text-brand"
          >
            <FileText className="h-3.5 w-3.5" aria-hidden />
            {file.filename ?? 'Attachment'}
            {file.byte_size ? (
              <span className="font-normal text-ink-3">
                {fileSize(file.byte_size)}
              </span>
            ) : null}
          </a>
        ) : (
          <img
            key={file.id}
            src={file.url ?? undefined}
            alt=""
            className="h-16 w-16 rounded object-cover"
          />
        ),
      )}
    </div>
  )
}

/** "2.4 MB" — worth knowing before tapping a download on a phone. */
function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** The two pick buttons. Styled labels wrapping hidden inputs — a native file
 *  input cannot be styled, but a label triggers one and can be. */
export function AttachButtons({
  onPick,
  disabled,
}: {
  onPick: (files: FileList | null, kind: 'image' | 'document') => void
  disabled?: boolean
}) {
  const imageId = useRef(`img-${Math.random().toString(36).slice(2)}`).current
  const docId = useRef(`doc-${Math.random().toString(36).slice(2)}`).current

  return (
    <div className="flex flex-wrap gap-s2">
      <input
        id={imageId}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          onPick(e.target.files, 'image')
          /* Cleared so picking the SAME file twice still fires a change. */
          e.target.value = ''
        }}
      />
      <PickLabel htmlFor={imageId} disabled={disabled}>
        <ImagePlus className="h-4 w-4" aria-hidden />
        Add a picture
      </PickLabel>

      <input
        id={docId}
        type="file"
        accept="application/pdf"
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          onPick(e.target.files, 'document')
          e.target.value = ''
        }}
      />
      <PickLabel htmlFor={docId} disabled={disabled}>
        <Paperclip className="h-4 w-4" aria-hidden />
        Add a PDF
      </PickLabel>
    </div>
  )
}

function PickLabel({
  htmlFor,
  disabled,
  children,
}: {
  htmlFor: string
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <label
      htmlFor={htmlFor}
      title={disabled ? `Up to ${MAX_FILES} files` : undefined}
      className={cn(
        'inline-flex items-center gap-s2 rounded-pill border border-line-2 px-s3 py-s2 text-sm font-semibold transition-colors',
        disabled
          ? 'cursor-not-allowed text-ink-3'
          : 'cursor-pointer text-ink-2 hover:border-brand hover:bg-brand-softer hover:text-brand',
      )}
    >
      {children}
    </label>
  )
}
